import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { event } from '@zillow/universal-analytics';
import { yieldCallback } from '@zillow/yield-callback';
import { withPreload, CardImage, usePageProps, getCmsInfoForPage, ColorCard } from '@zillow/zrs-cms';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { generateBlockList } from '../../components/BlockList/BlockList.js';
import { withTrigger } from '../../util/helpers.js';
import { populateParamPlaceholders } from '../../util/populateParamPlaceholders.js';

var _excluded = ["textAlign", "cardImage", "loading", "cardElevation", "cardDirection", "link", "backgroundColor", "fontColor", "appearance", "padding", "id"];
var CardImagePreload = withPreload(CardImage);
var BlockCard = function BlockCard(_ref) {
  var attrs = _ref.attrs,
    index = _ref.index,
    innerBlocks = _ref.innerBlocks;
  var pageProps = usePageProps();
  var _attrs$textAlign = attrs.textAlign,
    textAlign = _attrs$textAlign === void 0 ? 'inherit' : _attrs$textAlign,
    _attrs$cardImage = attrs.cardImage,
    cardImage = _attrs$cardImage === void 0 ? {} : _attrs$cardImage,
    _attrs$loading = attrs.loading,
    loading = _attrs$loading === void 0 ? 'lazy' : _attrs$loading,
    _attrs$cardElevation = attrs.cardElevation,
    cardElevation = _attrs$cardElevation === void 0 ? 1 : _attrs$cardElevation,
    _attrs$cardDirection = attrs.cardDirection,
    cardDirection = _attrs$cardDirection === void 0 ? 'bottom' : _attrs$cardDirection,
    link = attrs.link,
    backgroundColor = attrs.backgroundColor,
    _attrs$fontColor = attrs.fontColor,
    fontColor = _attrs$fontColor === void 0 ? 'textDark' : _attrs$fontColor,
    _attrs$appearance = attrs.appearance,
    appearance = _attrs$appearance === void 0 ? 'default' : _attrs$appearance,
    _attrs$padding = attrs.padding,
    padding = _attrs$padding === void 0 ? {
      paddingTop: {
        default: 'sm'
      },
      paddingBottom: {
        default: 'sm'
      },
      paddingRight: {
        default: 'sm'
      },
      paddingLeft: {
        default: 'sm'
      }
    } : _attrs$padding,
    id = attrs.id,
    rest = _objectWithoutProperties(attrs, _excluded);
  return /*#__PURE__*/React.createElement(StyledCardWithTrigger, _extends({
    key: index,
    id: id,
    style: {
      textAlign: textAlign
    },
    appearance: appearance,
    cardDirection: cardDirection,
    cardElevation: cardElevation,
    backgroundColor: backgroundColor,
    fontColor: fontColor,
    interactive: link !== null && link !== void 0 && link.url ? true : undefined,
    triggerLink: (link === null || link === void 0 ? void 0 : link.url) || link,
    onMouseEnter: function onMouseEnter(e) {
      event({
        // https://dataportal.zillowgroup.net/event-registry/2049
        envelope: {
          event_type_id: '2049',
          event_type_version_id: '1',
          event_template_id: '126',
          event_template_version_id: '1'
          // Master ID: 2284.2.126.1
        },
        clickstream_trigger: {
          trigger_type_nm: 'interaction',
          trigger_location_nm: 'cms',
          trigger_source_nm: 'cms_card',
          trigger_object_nm: 'no_trigger_object'
        },
        semantic: {
          semantic_event_nm: 'cms_hover'
        },
        cms_info: Object.assign({
          block_id: id || '',
          block_type_cd: 'card',
          block_displayed_txt: e.target.textContent,
          block_target_url: (link === null || link === void 0 ? void 0 : link.url) || '',
          block_target_window_txt: link !== null && link !== void 0 && link.opensInNewTab ? '_blank' : ''
        }, getCmsInfoForPage(pageProps))
      });
    },
    onClick: yieldCallback(function (e) {
      if (!(link !== null && link !== void 0 && link.url)) return;
      var url = populateParamPlaceholders(link.url);
      event({
        // https://dataportal.zillowgroup.net/event-registry/2048
        envelope: {
          event_type_id: '2048',
          event_type_version_id: '1',
          event_template_id: '126',
          event_template_version_id: '1'
          // Master ID: 2284.2.126.1
        },
        clickstream_trigger: {
          trigger_type_nm: 'interaction',
          trigger_location_nm: 'cms',
          trigger_source_nm: 'cms_card',
          trigger_object_nm: 'no_trigger_object'
        },
        semantic: {
          semantic_event_nm: 'cms_click'
        },
        cms_info: Object.assign({
          block_id: id || '',
          block_type_cd: 'card',
          block_displayed_txt: e.target.textContent,
          block_target_url: url,
          block_target_window_txt: link.opensInNewTab ? '_blank' : ''
        }, getCmsInfoForPage(pageProps))
      });
      if (link.opensInNewTab) {
        // eslint-disable-next-line no-undef
        window.open(url);
      } else {
        // eslint-disable-next-line no-undef
        window.location.href = url;
      }
    })
  }, padding, rest), cardImage.src && /*#__PURE__*/React.createElement(CardImagePreload, _extends({}, cardImage, {
    loading: loading,
    decoding: cardImage.decoding || 'async'
  })), generateBlockList({
    blocks: innerBlocks
  }));
};
BlockCard.propTypes = process.env.NODE_ENV !== "production" ? {
  attrs: PropTypes.shape({
    id: PropTypes.string,
    textAlign: PropTypes.string,
    cardImage: PropTypes.shape(CardImage.propTypes),
    loading: PropTypes.string,
    cardElevation: PropTypes.oneOf([0, 1]),
    cardDirection: PropTypes.oneOf(['bottom', 'top', 'left', 'right'])
  }),
  index: PropTypes.number,
  innerBlocks: PropTypes.arrayOf(PropTypes.shape({}))
} : {};

// Give the whole card textDark font color so the
// headings and paragraphs don't inherit the
// foreground of the group as font color.
var StyledCard = /*#__PURE__*/styled(ColorCard).withConfig({
  componentId: "sc-17txm87-0"
})(["height:100%;max-width:100%;"]);
var StyledCardWithTrigger = withTrigger(StyledCard);
var Card = BlockCard;

export { Card as default };
